import React, { useCallback } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import '../LyriaPay.css';
import WaitingBlock from '../WaitingBlock';
import { useTranslation } from 'react-i18next';
import ExpiryInput from '../Payline/ExpiryInput';
import PaymentsOSCardForm from './PaymentsOSCardForm';
import PaymentsOSCvvForm from './PaymentsOSCvvForm';

const PaymentsOS = React.forwardRef(
    (
        {
            config,
            token,
            lyriaUrl,
            savePaymentMethod,
            savedPaymentMethods = [],
            hidePayButton,
            onPaymentStarted,
            onPaymentResult,
            loader
        },
        ref
    ) => {

        const { t } = useTranslation();
        const [isFormVisible, showForm] = React.useState(true);
        const [isCardFormVisible, showCardForm] = React.useState(false);
        const [isSpinnerVisible, showSpinner] = React.useState(false);
        const [savePaymentMethodValue, setSavePaymentMethodValue] = React.useState(false);
        const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState(null);
        const [cardNumberCorrect, setCardNumberCorrect] = React.useState(null);
        const [expiryDateCorrect, setExpiryDateCorrect] = React.useState(null);
        const [cvvCorrect, setCvvCorrect] = React.useState(null);
        const paymentsOSCardFormRef = React.useRef();
        const paymentsOSCvvFormRef = React.useRef();

        React.useEffect(() => {
            if (savedPaymentMethods.length > 0) {
                setSelectedPaymentMethod(savedPaymentMethods[0].uuid);
            }
        }, [savedPaymentMethods]);

        React.useImperativeHandle(ref, () => ({
            pay() {
                if (selectedPaymentMethod) {
                    submitFormForPayingWithSavedCard();
                } else {
                    if (cardNumberCorrect && expiryDateCorrect && cvvCorrect) {
                        submitForm();
                    }
                }
            }
        }));

        const handleSuccess = () => {
            onPaymentResult({ success: true });
            showForm(false);
        };

        const handleError = response => {
            showForm(true);
            onPaymentResult({ success: false, data: response });
        };

        const handleChange = e => {
            const target = e.target;
            const value = target.checked;
            setSavePaymentMethodValue(value);
        };

        const handleOptionChange = e => {
            showCardForm(false);
            setSelectedPaymentMethod(e.target.value);
        };

        const handleChooseNewPaymentMethod = () => {
            showCardForm(true);
            setSelectedPaymentMethod(null);
        };

        const getExpiryMonthYear = (dateString) => {
            const parts = dateString.split('-');
            return {
                'month': parts[1],
                'year': parts[0]
            };
        };

        const handleClickOnPayWithSavedPaymentMethod = e => {
            e.preventDefault();
            submitFormForPayingWithSavedCard();
        };

        const onFormElementError = useCallback((error) => {
            if ('card' in error) {
                setCardNumberCorrect(!error.card);
            }
            if ('expiry' in error) {
                setExpiryDateCorrect(!error.expiry);
            }
            if ('cvv' in error) {
                setCvvCorrect(!error.cvv);
            }
        }, []);

        const onTokenizedCvv = (tokenizedCvv) => {
            if (!tokenizedCvv) {
                return false;
            }

            const tokenizedCvvJson = JSON.parse(tokenizedCvv);

            if (tokenizedCvvJson.category) {
                paymentsOSCvvFormRef.current.hasCvvError();
                return false;
            }

            showForm(false);
            showSpinner(true);

            axios({
                method: 'post',
                url: lyriaUrl + '/paymentsos/process',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: {
                    request_token: token,
                    psp: config.id,
                    cvx: tokenizedCvvJson.token,
                    saved_payment_method: selectedPaymentMethod
                }
            })
                .then(function({ data }) {
                    showSpinner(false);
                    if (data.status === 'V') {
                        handleSuccess();
                    } else if (data.status === 'A') {
                        handleError(data.data);
                    }
                })
                .catch(function(error) {
                    console.info(error);
                });
        };

        const savedCards = savedPaymentMethods && savedPaymentMethods.length > 0 ? savedPaymentMethods.map((spm) => (
            <div className="wz-lyriapay-payline-savedpaymentmethod" key={spm.uuid}>
                <label>
                    <input type="radio" value={spm.uuid} checked={selectedPaymentMethod === spm.uuid} onChange={handleOptionChange} />
                    {spm.card_type} {spm.masked_number}
                </label>
                {selectedPaymentMethod === spm.uuid && (
                    <>
                        <div className="wz-lyriapay-payline-flexcontainer">
                            <ExpiryInput
                                readOnly={true}
                                onChange={() => {}}
                                expiryAsText={true}
                                expiryMonthYear={getExpiryMonthYear(spm.expiry)}
                            />
                            <PaymentsOSCvvForm
                                config={config}
                                savedPaymentMethod={spm}
                                onResult={onTokenizedCvv}
                                ref={paymentsOSCvvFormRef}
                            />
                        </div>
                        <button
                            className="wz-lyriapay-formsubmit"
                            onClick={handleClickOnPayWithSavedPaymentMethod}
                            style={hidePayButton ? {
                            /*
                           note: we hide the button instead of removing it from the DOM
                           because the browser will trigger the buttonʼs onClick if we press Enter in any of the form input
                           it work if the button is hidden, it doesnʼt work if there is no button
                           */
                                display: 'none'
                            } : {}}
                        >
                            {t('payButton')}
                        </button>
                    </>
                )}
            </div>
        )): [];

        const submitFormForPayingWithSavedCard = () => {
            paymentsOSCvvFormRef.current.tokenize();
        };

        const submitForm = async () => {
            const cardHolderName = document.getElementById('cardholder-name').value;
            paymentsOSCardFormRef.current.clickedSubmit();
            if (cardHolderName !== '') {
                showForm(false);
                showSpinner(true);
                onPaymentStarted();
                const result = await paymentsOSCardFormRef.current.tokenize();

                axios({
                    method: 'post',
                    url: lyriaUrl + '/paymentsos/process',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    data: {
                        request_token: token,
                        psp: config.id,
                        save_payment_method: savePaymentMethodValue,
                        tokenized_data: JSON.parse(result)
                    }
                })
                    .then(function({ data }) {
                        showSpinner(false);
                        if (data.status === 'V') {
                            handleSuccess();
                        } else if (data.status === 'A') {
                            handleError(data.data);
                        }
                    })
                    .catch(function(error) {
                        console.info(error);
                    });
            }
        };

        const handleClick = e => {
            e.preventDefault();
            if (cardNumberCorrect && expiryDateCorrect && cvvCorrect) {
                submitForm();
            }
        };

        return (
            <div className="wz-lyriapay-form">
                {isSpinnerVisible && (
                    <WaitingBlock loader={loader}>
                        {t('waitingPaymentProcess')}
                    </WaitingBlock>
                )}
                {isFormVisible && (
                    <form noValidate id="payment-form">
                        {savedPaymentMethods.length > 0 && (
                            savedCards
                        )}
                        {savedPaymentMethods.length > 0 && !isCardFormVisible && (
                            <button
                                className="wz-lyriapay-payline-formsubmit"
                                onClick={handleChooseNewPaymentMethod}
                            >
                                {t('useNewPaymentMethod')}
                            </button>
                        )}
                        {(savedPaymentMethods.length === 0 || isCardFormVisible) && (
                            <>
                                <PaymentsOSCardForm
                                    config={config}
                                    onFormElementError={onFormElementError}
                                    ref={paymentsOSCardFormRef}
                                />
                                {savePaymentMethod && selectedPaymentMethod === null && (
                                    <div className="wz-lyriapay-paymentsos-savecard">
                                        <label>
                                            <input
                                                name="savepaymentmethod"
                                                value={savePaymentMethodValue}
                                                onChange={handleChange}
                                                type="checkbox"
                                            />
                                            {t('savePaymentMethod')}
                                        </label>
                                        <br/>
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    id="pay_button"
                                    className="wz-lyriapay-formsubmit"
                                    onClick={handleClick}
                                    style={hidePayButton ? {
                                        /*
                                note: we hide the button instead of removing it from the DOM
                                because the browser will trigger the buttonʼs onClick if we press Enter in any of the form
                                input it work if the button is hidden, it doesnʼt work if there is no button
                                */
                                        display: 'none'
                                    } : {}}>
                                    {t('payButton')}
                                </button>
                            </>
                        )}
                    </form>
                )}
            </div>
        );
    });

PaymentsOS.propTypes = {
    config: PropTypes.object,
    token: PropTypes.string.isRequired,
    lyriaUrl: PropTypes.string.isRequired,
    savePaymentMethod: PropTypes.bool,
    savedPaymentMethods: PropTypes.array,
    hidePayButton: PropTypes.bool,
    onPaymentStarted: PropTypes.func.isRequired,
    onPaymentResult: PropTypes.func.isRequired,
    loader: PropTypes.node,
};

PaymentsOS.displayName = 'PaymentsOS';

export default PaymentsOS;